import { useContext } from 'react';

import { isFiniteNumber } from '@core';
import { useToggle } from '@hooks';
import { ActionButton, Box, Text, TextProps } from '@ui';

import { LiveAnalysisTableContext, Parameter } from './context';
import { type WithIndex, LiveAnalysisTableCell } from './LiveAnalysisTableCell';
import * as styles from './styles.css';

export const calculateStatus = (corridorParameterMargins: Parameter, value: number): TextProps['color'] => {
    const { min = 0, max = 0 } = corridorParameterMargins;
    const range = max - min;
    const outerMax = max + range * 0.1;
    const outerMin = min - range * 0.1;
    if ((max === 0 && min === 0) || !isFiniteNumber(value)) {
        return 'brand';
    }

    if (value >= min && value <= max) return 'success';
    if ((value >= outerMin && value <= min) || (value <= outerMax && value >= max)) return 'warning';
    if (value > outerMax || value < outerMin) return 'danger';
    return 'brand';
};

export const LiveAnalysisTableRow = ({ index }: WithIndex) => {
    const { parameters, data, isSetup } = useContext(LiveAnalysisTableContext);
    const [isRowOpen, toggleIsRowOpen] = useToggle(false);
    // We display latest data on top
    const reverseDataIndex = data.length - index - 1;
    const rowData = data?.[reverseDataIndex];
    const columns = parameters.length;
    const rows = Object.entries(rowData).reduce((acc, param) => Math.max(param[1].length, acc), 0);

    return (
        <>
            <Box className={styles.rowOuter}>
                <Box className={styles.rowLabelCell}>
                    <Box className={styles.rowLabelHeader}>
                        {!isSetup && (
                            <ActionButton
                                icon={isRowOpen ? 'minus' : 'plus'}
                                variant="secondary"
                                iconSize="medium"
                                buttonSize="small"
                                onClick={() => toggleIsRowOpen()}
                            />
                        )}
                        <Text variant="h2">{reverseDataIndex + 1}</Text>
                    </Box>
                    {isRowOpen && !isSetup && (
                        <Box className={styles.rowExpandedList}>
                            {Array.from({ length: rows }).map((_, i) => (
                                <Box className={styles.setRow} key={i}>
                                    <Text variant="h3">{rows - i}</Text>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
            </Box>
            {Array.from({ length: columns }).map((_, index) => {
                const parameter = parameters?.[index];
                const entries = rowData[parameter.id];

                if (!parameter?.id || !entries?.length) {
                    return (
                        <LiveAnalysisTableCell key={index} index={index}>
                            <Box className={styles.cellAvgRow}>
                                <Text variant="h2">-</Text>
                                {!isSetup && <Box />}
                            </Box>
                            {isRowOpen && !isSetup && (
                                <Box className={styles.cellDetailList}>
                                    {Array.from({ length: rows }).map((_, i) => (
                                        <Box className={styles.setRow} key={i} />
                                    ))}
                                </Box>
                            )}
                        </LiveAnalysisTableCell>
                    );
                }

                const entriesCount = entries.length;

                const entriesAvg = entries.reduce((acc, entry) => acc + entry, 0) / (entriesCount || 1);

                const entriesHitRate = entries.reduce(
                    (acc, entry) => (entry >= parameter.min && entry <= parameter.max ? acc + 1 : acc),
                    0,
                );
                const status = calculateStatus(parameter, entriesAvg);
                return (
                    <LiveAnalysisTableCell key={index} index={index}>
                        <Box className={styles.cellAvgRow}>
                            <Text variant="h2" color={status} whiteSpace="nowrap">
                                {entriesCount ? `${entriesAvg.toFixed(1)}${parameter.displaySymbol}` : '-'}
                            </Text>
                            {!isSetup && (
                                <Text variant="h2">
                                    {entriesHitRate}/{entriesCount}
                                </Text>
                            )}
                        </Box>
                        {isRowOpen && !isSetup && (
                            <Box className={styles.cellDetailList}>
                                {Array.from({ length: rows }).map((_, i) => {
                                    const entryNo = rows - i;
                                    const entryIndex = entryNo - 1;
                                    const entry = entries[entryIndex];
                                    const hasEntry = typeof entry !== 'undefined';
                                    return (
                                        <Box className={styles.cellAvgRow} key={i}>
                                            {hasEntry && (
                                                <>
                                                    <Text
                                                        variant="h2"
                                                        whiteSpace="nowrap"
                                                        color={calculateStatus(parameter, entry)}
                                                    >
                                                        {entry}
                                                        {parameter.displaySymbol}
                                                    </Text>
                                                    <Box />
                                                </>
                                            )}
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}
                    </LiveAnalysisTableCell>
                );
            })}
        </>
    );
};

export const LiveAnalysisTableFooter = () => {
    const { parameters, sum, isSetup } = useContext(LiveAnalysisTableContext);
    const [isRowOpen, toggleIsRowOpen] = useToggle(false);
    // We display latest data on top
    const rowData = sum;
    const columns = parameters.length;
    const rows = Object.entries(rowData).reduce((acc, param) => Math.max(param[1].length, acc), 0);

    return (
        <>
            <Box className={styles.rowOuter}>
                <Box className={styles.rowLabelCell}>
                    <Box className={styles.rowLabelHeader}>
                        {!isSetup && (
                            <ActionButton
                                icon={isRowOpen ? 'minus' : 'plus'}
                                variant="secondary"
                                iconSize="medium"
                                buttonSize="small"
                                onClick={() => toggleIsRowOpen()}
                            />
                        )}
                        <Text variant="h2">=</Text>
                    </Box>
                    {isRowOpen && !isSetup && (
                        <Box className={styles.rowExpandedList}>
                            {Array.from({ length: rows }).map((_, i) => (
                                <Box className={styles.setRow} key={i}>
                                    <Text variant="h3">{rows - i}</Text>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
            </Box>
            {Array.from({ length: columns }).map((_, index) => {
                const parameter = parameters?.[index];
                const entries = rowData[parameter.id];

                if (!parameter?.id || !entries?.length) {
                    return (
                        <LiveAnalysisTableCell key={index} index={index}>
                            <Box className={styles.cellAvgRow}>
                                <Text variant="h2">-</Text>
                                {!isSetup && <Box />}
                            </Box>
                            {isRowOpen && !isSetup && (
                                <Box className={styles.cellDetailList}>
                                    {Array.from({ length: rows }).map((_, i) => (
                                        <Box className={styles.setRow} key={i} />
                                    ))}
                                </Box>
                            )}
                        </LiveAnalysisTableCell>
                    );
                }

                const entriesCount = entries.length;

                const entriesAvg = entries.reduce((acc, entry) => acc + entry, 0) / (entriesCount || 1);

                const entriesHitRate = entries.reduce(
                    (acc, entry) => (entry >= parameter.min && entry <= parameter.max ? acc + 1 : acc),
                    0,
                );
                const status = calculateStatus(parameter, entriesAvg);
                return (
                    <LiveAnalysisTableCell key={index} index={index}>
                        <Box className={styles.cellAvgRow}>
                            <Text variant="h2" color={status} whiteSpace="nowrap">
                                {entriesCount ? `${entriesAvg.toFixed(1)}${parameter.displaySymbol}` : '-'}
                            </Text>
                            {!isSetup && (
                                <Text variant="h2">
                                    {entriesHitRate}/{entriesCount}
                                </Text>
                            )}
                        </Box>
                        {isRowOpen && !isSetup && (
                            <Box className={styles.cellDetailList}>
                                {Array.from({ length: rows }).map((_, i) => {
                                    const entryNo = rows - i;
                                    const entryIndex = entryNo - 1;
                                    const entry = entries[entryIndex];
                                    const hasEntry = typeof entry !== 'undefined';
                                    return (
                                        <Box className={styles.cellAvgRow} key={i}>
                                            {hasEntry && (
                                                <>
                                                    <Text
                                                        variant="h2"
                                                        whiteSpace="nowrap"
                                                        color={calculateStatus(parameter, entry)}
                                                    >
                                                        {entry}
                                                        {parameter.displaySymbol}
                                                    </Text>
                                                    <Box />
                                                </>
                                            )}
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}
                    </LiveAnalysisTableCell>
                );
            })}
        </>
    );
};
